import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  ICreateQuickActivityRequest,
  IToggleQuickActivityStatusRequest,
  IUpdateQuickActivityRequest,
  TCreateQuickActivityResponse,
  TDeleteQuickActivityResponse,
  TGetQuickActivitiesPaginatedRequest,
  TGetQuickActivitiesPaginatedResponse,
  TGetQuickActivitiesRequest,
  TGetQuickActivitiesResponse,
  TGetQuickActivityByIdRequest,
  TGetQuickActivityByIdResponse,
  TToggleQuickActivityStatusResponse,
  TUpdateQuickActivityResponse,
} from "@/types/(company)/quickActivity.interface";

const { QuickActivity } = APIEndpoints.Company.settings;

export const companyQuickActivityApi = createApi({
  reducerPath: "companyQuickActivityApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["QuickActivities"],
  endpoints: (builder) => ({
    getQuickActivities: builder.query<
      TGetQuickActivitiesResponse,
      TGetQuickActivitiesRequest
    >({
      query: (params) => ({
        url: QuickActivity.GetAll,
        method: "GET",
        params,
      }),
      providesTags: ["QuickActivities"],
    }),

    getQuickActivitiesPaginated: builder.query<
      TGetQuickActivitiesPaginatedResponse,
      TGetQuickActivitiesPaginatedRequest
    >({
      query: (params) => ({
        url: QuickActivity.GetPaginated,
        method: "GET",
        params,
      }),
      providesTags: ["QuickActivities"],
    }),

    getQuickActivityById: builder.query<
      TGetQuickActivityByIdResponse,
      TGetQuickActivityByIdRequest
    >({
      query: ({ id }) => ({
        url: QuickActivity.GetById(id),
        method: "GET",
      }),
      providesTags: ["QuickActivities"],
    }),

    createQuickActivity: builder.mutation<
      TCreateQuickActivityResponse,
      ICreateQuickActivityRequest
    >({
      query: (body) => ({
        url: QuickActivity.Create,
        method: "POST",
        body,
      }),
      invalidatesTags: ["QuickActivities"],
    }),

    updateQuickActivity: builder.mutation<
      TUpdateQuickActivityResponse,
      IUpdateQuickActivityRequest
    >({
      query: ({ id, data }) => ({
        url: QuickActivity.Update(id),
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["QuickActivities"],
    }),

    toggleQuickActivityStatus: builder.mutation<
      TToggleQuickActivityStatusResponse,
      IToggleQuickActivityStatusRequest
    >({
      query: ({ id }) => ({
        url: QuickActivity.ToggleStatus(id),
        method: "PATCH",
      }),
      invalidatesTags: ["QuickActivities"],
    }),

    deleteQuickActivity: builder.mutation<
      TDeleteQuickActivityResponse,
      number
    >({
      query: (id) => ({
        url: QuickActivity.Delete(id),
        method: "DELETE",
      }),
      invalidatesTags: ["QuickActivities"],
    }),
  }),
});

export const {
  useGetQuickActivitiesQuery,
  useGetQuickActivitiesPaginatedQuery,
  useGetQuickActivityByIdQuery,
  useCreateQuickActivityMutation,
  useUpdateQuickActivityMutation,
  useToggleQuickActivityStatusMutation,
  useDeleteQuickActivityMutation,
} = companyQuickActivityApi;